import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

interface RadioMessage {
  id?: number;
  timestamp: string;
  indicatif_emetteur: string;
  destinataire: string;
  message: string;
  statut_radio: number | null;
  phase: string | null;
  is_cos_message: 0 | 1;
}

interface Props {
  instanceId: number;
  /** Incrémenter pour forcer un rechargement */
  refreshKey?: number;
}

/** Libellés des statuts ANTARES affichés dans le journal */
const STATUT_LABELS: Record<number, string> = {
  1: 'DISPONIBLE',
  2: 'EN ROUTE',
  3: 'SUR PLACE',
  4: 'DISPONIBLE RADIO',
  5: 'EN REPOS',
  6: 'NON OPÉRATIONNEL',
  7: 'DISPONIBLE RADIO',
  8: 'EN MAINTENANCE',
};

/** Couleur de l'indicatif selon le statut */
const STATUT_COLORS: Record<number, string> = {
  1: 'text-emerald-400',
  2: 'text-amber-300',
  3: 'text-cyan-300',
  4: 'text-emerald-300',
  5: 'text-slate-400',
  6: 'text-red-400',
  7: 'text-emerald-300',
  8: 'text-orange-400',
};

/** Badge de phase */
const PHASE_BADGE: Record<string, { label: string; cls: string }> = {
  arrivee_confirmation: { label: 'ARRIVÉE', cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
  ambiance_cos:        { label: 'COS', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  bilan_victime:       { label: 'TRANSPORT', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  fin:                 { label: 'FIN', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
};

function formatTime(ts: string): string {
  try {
    return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return ts;
  }
}

const RadioJournalDispatch: React.FC<Props> = ({ instanceId, refreshKey }) => {
  const [messages, setMessages] = useState<RadioMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchJournal = useCallback(async () => {
    try {
      const base = getApiBaseUrl();
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
      const res = await fetch(`${base}/api/cta/interventions-instance/${instanceId}/radio-journal`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      const data = await res.json();
      setMessages(data.journal ?? []);
    } catch {
      /* silencieux */
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    void fetchJournal();
  }, [fetchJournal, refreshKey]);

  /* Auto-scroll vers le bas à chaque nouveau message */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /* Auto-refresh toutes les 15s pendant l'intervention */
  useEffect(() => {
    const id = setInterval(() => void fetchJournal(), 15_000);
    return () => clearInterval(id);
  }, [fetchJournal]);

  if (loading && !messages.length) {
    return (
      <div className="flex items-center justify-center py-6">
        <p className="text-[10px] text-slate-600 animate-pulse">Chargement du journal…</p>
      </div>
    );
  }

  if (!messages.length) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-3 text-center">
        <svg className="w-6 h-6 text-slate-700 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
        </svg>
        <p className="text-[10px] text-slate-600 italic leading-relaxed">
          Le journal s'alimentera au fur et à mesure<br />de l'avancement de l'intervention.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-px py-1 px-1">
      {messages.map((m, i) => {
        const phaseBadge = m.phase ? PHASE_BADGE[m.phase] : null;
        const indicatifColor = m.statut_radio != null
          ? (STATUT_COLORS[m.statut_radio] ?? 'text-slate-300')
          : (m.is_cos_message ? 'text-amber-200' : 'text-slate-300');

        return (
          <div
            key={m.id ?? i}
            className={`rounded px-2 py-1.5 text-[10px] leading-snug font-mono ${
              m.is_cos_message
                ? 'bg-amber-950/20 border border-amber-700/20'
                : m.statut_radio != null
                  ? 'bg-slate-900/40 border border-slate-800/50'
                  : 'bg-transparent'
            }`}
          >
            {/* Ligne 1 : heure + indicatif + badge phase/statut */}
            <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
              <span className="text-slate-600 tabular-nums shrink-0">{formatTime(m.timestamp)}</span>
              <span className={`font-bold truncate max-w-[7rem] shrink-0 ${indicatifColor}`}>
                {m.indicatif_emetteur}
              </span>
              {phaseBadge && (
                <span className={`rounded border px-1 py-0 text-[9px] font-semibold uppercase tracking-wide leading-4 ${phaseBadge.cls}`}>
                  {phaseBadge.label}
                </span>
              )}
              {m.statut_radio != null && STATUT_LABELS[m.statut_radio] && (
                <span className={`rounded border border-current/20 bg-current/5 px-1 py-0 text-[9px] font-semibold uppercase tracking-wide leading-4 ${indicatifColor}`}>
                  {STATUT_LABELS[m.statut_radio]}
                </span>
              )}
            </div>
            {/* Ligne 2 : texte du message */}
            <p className="text-slate-400 break-words leading-snug pl-0">
              {m.message}
            </p>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
};

export default RadioJournalDispatch;
